import dayjs from "dayjs";
import { TICKET_STATUS } from "../../store/constants";
import { formatNumber, formatTime } from "../../store/helpers";

const statusLabels = {
  [TICKET_STATUS.IN_QUEUE]: "In queue",
  [TICKET_STATUS.ACTIVE]: "Active",
  [TICKET_STATUS.NO_SHOW]: "No show",
  [TICKET_STATUS.COMPLETED]: "Completed",
};

const getTicketNumber = (ticket = {}) => {
  if (!ticket.ticketNumber) return "NA";
  return ticket.ticketLetter ? `${ticket.ticketLetter}-${ticket.ticketNumber}` : ticket.ticketNumber;
};

const getStatusLabel = (status) => statusLabels[Number(status)] || "Unknown";

const getDurationDisplay = (duration) => {
  if (duration === null || duration === undefined || Number.isNaN(Number(duration))) return "NA";
  return formatTime(Number(duration));
};

const getCustomerName = (customer = {}) =>
  customer.fullname ||
  [customer.name, customer.lastname].filter(Boolean).join(" ") ||
  customer.mobileNumber ||
  customer.phone ||
  "Unnamed customer";

const getTopRows = (items, idKey, nameKey, countKey, limit = 5) =>
  [...items]
    .sort((a, b) => b[countKey] - a[countKey] || String(a[nameKey]).localeCompare(String(b[nameKey])))
    .slice(0, limit)
    .map((item, index) => ({
      ...item,
      [idKey]: item[idKey] || `${item[nameKey]}-${index}`,
    }));

export const buildTicketLookupSummary = (data) => {
  if (!data?.ticket) return null;

  const { customer, agentFullName, serviceName, ticket, totalTimeInBranch, waitingTime } = data;
  const serviceTime =
    Number(ticket.status) === TICKET_STATUS.COMPLETED ? ticket.sessionInSeconds : null;

  return {
    kpis: [
      { label: "Ticket", value: getTicketNumber(ticket) },
      { label: "Status", value: getStatusLabel(ticket.status) },
      { label: "Service", value: serviceName || "NA" },
      { label: "Agent", value: agentFullName || "NA" },
    ],
    tables: [
      {
        title: "Ticket Preview",
        columns: [
          { id: "metric", label: "Metric" },
          { id: "value", label: "Value" },
        ],
        rows: [
          { metric: "Customer", value: getCustomerName(customer) },
          {
            metric: "Created",
            value: ticket.created ? dayjs.unix(ticket.created).format("MMM D, YYYY h:mm A") : "NA",
          },
          { metric: "Waiting Time", value: getDurationDisplay(waitingTime) },
          { metric: "Service Time", value: getDurationDisplay(serviceTime) },
          { metric: "Time In Branch", value: getDurationDisplay(totalTimeInBranch) },
        ],
      },
    ],
    notes: ["Preview reflects the current ticket lookup result."],
  };
};

export const buildTicketsByDateSummary = ({
  averageTotalTimeInBranch,
  chart = [],
  data = [],
  maxTicketsPerHour,
  numCompleted,
  numNoShow,
  numTickets,
  numUnattended,
  peakHour,
  selectedTicketsStatus,
}) => {
  if (!data.length) return null;

  const serviceRows = Object.values(
    data.reduce((acc, item) => {
      const serviceName = item.serviceName || "Unknown service";
      acc[serviceName] = acc[serviceName] || { serviceName, tickets: 0 };
      acc[serviceName].tickets += 1;
      return acc;
    }, {})
  );
  const statusRows = [
    { status: "Completed", tickets: numCompleted },
    { status: "Unattended", tickets: numUnattended },
    { status: "No show", tickets: numNoShow },
  ];
  const hourlyRows = getTopRows(
    chart
      .filter((item) => item.hourTickets > 0)
      .map((item) => ({
        hour: item.hour,
        tickets: item.hourTickets,
      })),
    "hourId",
    "hour",
    "tickets",
    5
  );

  const notes = selectedTicketsStatus
    ? [`Summary reflects the current ${getStatusLabel(selectedTicketsStatus).toLowerCase()} filter.`]
    : [];

  return {
    kpis: [
      { label: "Tickets", value: formatNumber(numTickets) },
      { label: "Completed", value: formatNumber(numCompleted) },
      {
        label: "Average Time In Branch",
        value:
          Number.isFinite(averageTotalTimeInBranch) && !Number.isNaN(averageTotalTimeInBranch)
            ? formatTime(averageTotalTimeInBranch)
            : "NA",
      },
      { label: "Peak Arrival Hour", value: peakHour },
      {
        label: "Max Visitors Per Hour",
        value:
          Number.isFinite(maxTicketsPerHour) && !Number.isNaN(maxTicketsPerHour)
            ? formatNumber(maxTicketsPerHour)
            : "NA",
      },
    ],
    tables: [
      {
        title: "Status Breakdown",
        columns: [
          { id: "status", label: "Status" },
          { id: "tickets", label: "Tickets" },
        ],
        rows: statusRows,
        renderCell: (row, columnId) =>
          columnId === "tickets" ? formatNumber(row[columnId]) : row[columnId],
      },
      {
        title: "Top Services",
        columns: [
          { id: "serviceName", label: "Service" },
          { id: "tickets", label: "Tickets" },
        ],
        rows: getTopRows(serviceRows, "serviceId", "serviceName", "tickets"),
        renderCell: (row, columnId) =>
          columnId === "tickets" ? formatNumber(row[columnId]) : row[columnId],
      },
      {
        title: "Busiest Hours",
        columns: [
          { id: "hour", label: "Hour" },
          { id: "tickets", label: "Tickets" },
        ],
        rows: hourlyRows,
        renderCell: (row, columnId) =>
          columnId === "tickets" ? formatNumber(row[columnId]) : row[columnId],
      },
    ].filter((table) => table.rows.length),
    notes,
  };
};

export const buildExportDataSummary = ({ branchDisplay, exportCount, periodDisplay, timeUnit }) => ({
  kpis: [
    { label: "Records", value: `${formatNumber(exportCount)} tickets` },
    { label: "Period", value: periodDisplay },
    { label: "Scope", value: branchDisplay },
    { label: "Time Columns", value: timeUnit === "minutes" ? "Minutes" : "Seconds" },
  ],
  tables: [
    {
      title: "Export Preview",
      columns: [
        { id: "field", label: "Field" },
        { id: "value", label: "Value" },
      ],
      rows: [
        { field: "Period", value: periodDisplay },
        { field: "Branch", value: branchDisplay },
        { field: "Records", value: `${formatNumber(exportCount)} tickets` },
        { field: "Duration Format", value: timeUnit === "minutes" ? "Minutes" : "Seconds" },
      ],
    },
  ],
  notes: ["Download uses the selected branch, period, and time format."],
});
